// ═══════════════════════════════════════════════════════════════════════
// Suivi « prochain acteur » — composants communs (contrat : docs/rh/workflows/CONTRAT_PROCHAIN_ACTEUR.md).
//
// Règle du chef de projet : chaque demande ou dossier avec workflow affiche son statut, le prochain acteur
// (personne + fonction) et la durée d'attente. LE BACKEND EST LA SOURCE : ces composants n'affichent que l'objet
// `suivi` renvoyé par l'API (vue SALARIE ou GESTION). Ils ne déduisent aucun valideur, ne recalculent aucune
// attente et ne traduisent aucun statut ; ils assemblent seulement les libellés reçus. Tolérants aux réponses
// anciennes : `suivi` absent ou partiel → rien (ou le libellé reçu tel quel), jamais d'erreur.
// Aucun jargon IAM : ni clé de permission, ni code de rôle, ni `gestion.regle` ne sont affichés.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Hourglass, UserX, CheckCircle2, AlertTriangle } from 'lucide-react';
import { formaterDate } from '../../design/composants';
import './suivi.css';

/** Libellé de vacance du contrat (§ 3.4) — repli si le serveur ne l'a pas fourni. */
export const LIBELLE_VACANCE = 'Validation en attente — aucun titulaire actif pour cette fonction';

const QUALITES = { INTERIM: 'par intérim', DELEGATION: 'par délégation', REMPLACANT: 'en remplacement' };

const txt = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
/**
 * Aucun jargon IAM à l'écran : une explication qui cite un identifiant technique (clé de permission
 * « peut_approuver_pret_sogas », nom de fichier, chemin de route) n'est pas affichée.
 */
export const sansJargon = (v) => {
  const t = txt(v);
  return t && !/\b[a-z0-9]+(?:_[a-z0-9]+)+\b|\.js\b|\/api\/|routes\//.test(t) ? t : null;
};
const estVueGestion = (s) => !!(s && s.gestion);

/**
 * Lecture du `suivi` pour l'affichage : { genre, acteur, fonction, attente, titre, detail }.
 * genre : 'termine' | 'vacance' | 'demandeur' | 'attente'. Aucune règle métier : assemblage des champs reçus.
 */
export function lireSuivi(suivi) {
  if (!suivi || typeof suivi !== 'object') return null;
  const p = suivi.prochain_acteur || {};
  const etape = suivi.etape || {};
  const fonction = txt(p.fonction && p.fonction.libelle);
  const nom = txt(p.personne && p.personne.nom_affiche);
  const attente = txt(etape.attente_libelle);
  const raison = sansJargon(p.raison);
  // Terminé, ou étape sans acteur humain (AUCUN : ex. remboursement en cours) : aucun prochain acteur à afficher.
  if (suivi.termine || p.type === 'AUCUN') {
    return { genre: 'termine', titre: txt(suivi.libelle_salarie) || txt(suivi.statut_libelle) || 'Demande terminée', detail: null, acteur: null, fonction: null, attente: null };
  }
  if (p.vacance) {
    return { genre: 'vacance', titre: txt(suivi.libelle_salarie) || LIBELLE_VACANCE, acteur: null, fonction, attente,
      detail: fonction ? `Fonction attendue : ${fonction}` : null };
  }
  if (p.type === 'DEMANDEUR') {
    const gestion = estVueGestion(suivi);
    return { genre: 'demandeur', acteur: gestion ? 'Le demandeur' : 'Vous', fonction: null, attente,
      titre: gestion ? `En attente du demandeur${raison ? ` — ${raison}` : ''}` : (txt(suivi.libelle_salarie) || 'À faire de votre part'),
      detail: null };
  }
  const qualite = QUALITES[p.qualite] || null;
  const jusquAu = p.mandat && p.mandat.jusqu_au ? `jusqu'au ${formaterDate(p.mandat.jusqu_au, true)}` : null;
  const complement = [qualite, jusquAu].filter(Boolean).join(' ');
  if (p.type === 'PERSONNE' && nom) {
    return { genre: 'attente', acteur: nom, fonction, attente,
      titre: `En attente de ${nom}${fonction ? ` — ${fonction}` : ''}`,
      detail: [complement && `Agit ${complement}.`, raison].filter(Boolean).join(' ') || null };
  }
  if (p.type === 'FONCTION' && fonction) {
    return { genre: 'attente', acteur: null, fonction, attente, titre: `En attente — ${fonction}`, detail: raison };
  }
  // Type inconnu (version ultérieure) ou réponse partielle : le libellé du serveur tel quel (contrat § 10).
  const titre = txt(suivi.libelle_salarie) || txt(etape.libelle) || txt(suivi.statut_libelle);
  return titre ? { genre: 'attente', acteur: nom, fonction, attente, titre, detail: raison } : null;
}

/** Une action est-elle attendue de quelqu'un (dossier ni terminé, ni sans acteur) ? Affichage seulement. */
export const suiviEnCours = (suivi) => { const l = lireSuivi(suivi); return !!l && l.genre !== 'termine'; };

const ICONES = { termine: CheckCircle2, vacance: UserX, demandeur: AlertTriangle, attente: Hourglass };
const CLASSES = { termine: 'sv-termine', vacance: 'sv-vacance', demandeur: 'sv-demandeur', attente: '' };
const ETATS = { FAIT: { classe: 'sv-fait', signe: '✓', libelle: 'Fait' }, EN_COURS: { classe: 'sv-en-cours', signe: '', libelle: 'En cours' }, A_VENIR: { classe: 'sv-a-venir', signe: '', libelle: 'À venir' } };

/** Circuit vertical : ✓ fait · ● en cours · ○ à venir, avec l'acteur et la date reçus. */
export function CircuitSuivi({ circuit }) {
  const etapes = Array.isArray(circuit) ? circuit.filter((e) => e && typeof e === 'object') : [];
  if (!etapes.length) return null;
  return (
    <ol className="sv-circuit" aria-label="Circuit de validation">
      {etapes.map((e, i) => {
        const etat = ETATS[e.etat] || ETATS.A_VENIR;
        const date = e.date ? formaterDate(e.date, true) : null;
        const meta = [txt(e.acteur_nom), date && (e.etat === 'FAIT' ? `le ${date}` : `depuis le ${date}`)].filter(Boolean).join(' · ');
        return (
          <li key={`${e.etape || 'etape'}-${i}`} className={etat.classe}>
            <span className="sv-puce" aria-hidden="true">{etat.signe}</span>
            <span className="sv-etape">
              <span className="sv-etape-libelle">{txt(e.libelle) || txt(e.etape) || 'Étape'}</span>
              <span className="sv-sr"> — {etat.libelle}</span>
              {meta && <span className="sv-etape-meta" style={{ display: 'block' }}>{meta}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Autres personnes habilitées (vue GESTION seulement) : noms et fonction, jamais d'identifiant ni de règle IAM. */
function AutresActeurs({ suivi }) {
  const autres = suivi && suivi.gestion && Array.isArray(suivi.gestion.autres_acteurs) ? suivi.gestion.autres_acteurs : [];
  const noms = autres.map((a) => txt(a && a.nom_affiche)).filter(Boolean);
  if (!noms.length) return null;
  const visibles = noms.slice(0, 4);
  return <div className="sv-bandeau-detail">Peuvent aussi agir : {visibles.join(', ')}{noms.length > visibles.length ? ` (+${noms.length - visibles.length})` : ''}</div>;
}

/**
 * Bandeau « En attente de <Nom> — <Fonction> — Depuis N jours » (+ circuit).
 * circuit : 'ouvert' (affiché), 'replie' (bouton « Voir le circuit »), 'aucun'.
 */
export function SuiviDemande({ suivi, circuit = 'replie', style }) {
  const [ouvert, setOuvert] = useState(circuit === 'ouvert');
  const l = lireSuivi(suivi);
  if (!l) return null;
  const Icone = ICONES[l.genre] || Hourglass;
  const aCircuit = circuit !== 'aucun' && Array.isArray(suivi.circuit) && suivi.circuit.length > 0;
  return (
    <div className={`sv-bandeau ${CLASSES[l.genre] || ''}`} role="status" style={style}>
      <Icone size={16} aria-hidden="true" />
      <div className="sv-bandeau-corps">
        <div className="sv-bandeau-titre">
          {l.titre}{l.attente && <span className="sv-attente"> — {l.attente}</span>}
        </div>
        {l.detail && <div className="sv-bandeau-detail">{l.detail}</div>}
        {l.genre === 'attente' && <AutresActeurs suivi={suivi} />}
        {aCircuit && circuit === 'replie' && (
          <button type="button" className="sv-bouton-circuit" aria-expanded={ouvert} onClick={(e) => { e.stopPropagation(); setOuvert((o) => !o); }}>
            {ouvert ? 'Masquer le circuit' : 'Voir le circuit'}
          </button>
        )}
        {aCircuit && ouvert && <CircuitSuivi circuit={suivi.circuit} />}
      </div>
    </div>
  );
}

/** Cellule « Doit agir » : personne + fonction, vacance mise en évidence, « — » si terminé ou inconnu. */
export function DoitAgir({ suivi }) {
  const l = lireSuivi(suivi);
  if (!l || l.genre === 'termine') return <span className="sv-cellule-fonction">—</span>;
  if (l.genre === 'vacance') {
    return (
      <span>
        <span className="sv-cellule-nom sv-ligne-vacance">Aucun titulaire actif</span>
        {l.fonction && <span className="sv-cellule-fonction">{l.fonction}</span>}
      </span>
    );
  }
  return (
    <span>
      <span className="sv-cellule-nom">{l.acteur || l.fonction || l.titre}</span>
      {l.acteur && l.fonction && <span className="sv-cellule-fonction">{l.fonction}</span>}
    </span>
  );
}

/** Cellule « Depuis » : le libellé d'attente reçu (« Depuis 2 jours »). */
export function DepuisSuivi({ suivi }) {
  const l = lireSuivi(suivi);
  return <span className="sv-cellule-fonction" style={{ whiteSpace: 'nowrap' }}>{(l && l.attente) || '—'}</span>;
}

/**
 * Ligne compacte pour les listes (cartes, boîtes « à traiter ») : « Doit agir : X — Fonction · Depuis N jours »,
 * avec le circuit dépliable. Rien si le dossier est terminé ou si `suivi` est absent.
 */
export function LigneSuivi({ suivi }) {
  const [ouvert, setOuvert] = useState(false);
  const l = lireSuivi(suivi);
  if (!l || l.genre === 'termine') return null;
  const aCircuit = Array.isArray(suivi.circuit) && suivi.circuit.length > 0;
  const qui = l.genre === 'vacance'
    ? <span className="sv-ligne-vacance">{LIBELLE_VACANCE}{l.fonction ? ` (${l.fonction})` : ''}</span>
    : l.genre === 'demandeur' ? <strong>{l.titre}</strong>
      : <>Doit agir : <strong>{l.acteur || l.fonction || l.titre}</strong>{l.acteur && l.fonction ? ` — ${l.fonction}` : ''}</>;
  return (
    <span className="sv-ligne-compacte">
      {qui}{l.attente ? ` · ${l.attente}` : ''}
      {aCircuit && (
        <>
          {' '}
          <button type="button" className="sv-bouton-circuit" aria-expanded={ouvert} onClick={(e) => { e.stopPropagation(); setOuvert((o) => !o); }}>
            {ouvert ? 'Masquer le circuit' : 'Circuit'}
          </button>
        </>
      )}
      {aCircuit && ouvert && <CircuitSuivi circuit={suivi.circuit} />}
    </span>
  );
}

/** Rôles à portée globale qui ouvrent la vue « Dossiers en attente » (contrat § 6 : utils/perimetre.ROLES_GLOBAL). */
const ROLES_GLOBAL = ['admin', 'directeur_general', 'directeur_adjoint', 'drh', 'rh_principal', 'assistant_rh'];

/**
 * Affichage seulement (entrée de menu) : l'utilisateur peut-il ouvrir la vue « Dossiers en attente » ?
 * Le serveur reste juge (403 sinon).
 */
export function peutVoirDossiersEnAttente(employe) {
  if (!employe || employe.nature === 'EXTERNE') return false;
  return ROLES_GLOBAL.includes(employe.role) || (employe.permissions_effectives || {}).voir_dossiers_en_attente === true;
}

/**
 * « Mes demandes » du salarié (GET /api/moi/demandes) : identifiant du dossier → `suivi` (vue SALARIE).
 * Sert aux écrans web dont la liste d'origine n'est pas enrichie (congés, permissions, arrêts).
 */
export function useSuiviMesDemandes(actif = true, cle = 0) {
  const [carte, setCarte] = useState(() => new Map());
  useEffect(() => {
    if (!actif) return undefined;
    let vivant = true;
    axios.get('/api/moi/demandes')
      .then((r) => {
        const d = r && r.data && (r.data.data || r.data);
        const liste = Array.isArray(d && d.demandes) ? d.demandes : [];
        const m = new Map();
        liste.forEach((x) => { if (x && x.id && x.suivi) m.set(String(x.id), x.suivi); });
        if (vivant) setCarte(m);
      })
      .catch(() => { if (vivant) setCarte(new Map()); });
    return () => { vivant = false; };
  }, [actif, cle]);
  return carte;
}

/**
 * Suivi d'un dossier lu à la demande : vue GESTION (GET /api/workflow/suivi/:type/:id) ou SALARIE
 * (GET /api/moi/suivi/:type/:id). Silencieux si la route refuse ou si le dossier est introuvable.
 */
export function SuiviDossier({ type, id, vue = 'GESTION', actif = true, circuit = 'ouvert', cle = 0 }) {
  const [suivi, setSuivi] = useState(null);
  useEffect(() => {
    if (!actif || !type || !id) { setSuivi(null); return undefined; }
    let vivant = true;
    const base = vue === 'SALARIE' ? '/api/moi/suivi' : '/api/workflow/suivi';
    axios.get(`${base}/${encodeURIComponent(type)}/${encodeURIComponent(id)}`)
      .then((r) => { if (vivant) setSuivi((r && r.data && r.data.data) || null); })
      .catch(() => { if (vivant) setSuivi(null); });
    return () => { vivant = false; };
  }, [type, id, vue, actif, cle]);
  return <SuiviDemande suivi={suivi} circuit={circuit} />;
}

/**
 * Détail d'un dossier côté gestion (fiches des dispositifs sociaux…). Aucune condition de droit côté client : le
 * serveur décide (dispositifs : lecture par mandat ou contrôle ; autres types : droit « Dossiers en attente ») et un
 * refus (403) ou un dossier introuvable (404) n'affiche simplement rien. Si la fiche porte déjà `suivi`, il est
 * utilisé sans nouvel appel. `cle` : changer sa valeur relit le suivi (après une action).
 */
export function SuiviDossierGestion({ type, id, cle, suivi }) {
  if (suivi !== undefined) return <SuiviDemande suivi={suivi} circuit="replie" />;
  return <SuiviDossier type={type} id={id} vue="GESTION" circuit="replie" cle={cle} />;
}

export default SuiviDemande;
